import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Users, Plus, MessageSquare, TrendingUp, Lock, Globe } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import InviteButton from '@/components/social/InviteButton';

export default function Groups() {
  const [user, setUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    group_type: 'general',
    is_public: true
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.list('-created_date'),
  });

  const { data: memberships = [] } = useQuery({
    queryKey: ['group-memberships', user?.id],
    queryFn: () => base44.entities.GroupMembership.filter({ user_id: user?.id }),
    enabled: !!user?.id,
  });

  const createGroupMutation = useMutation({
    mutationFn: async (data) => {
      const group = await base44.entities.Group.create(data);
      // Auto-join as admin
      await base44.entities.GroupMembership.create({
        group_id: group.id,
        user_id: user.id,
        role: 'admin'
      });
      return group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-memberships'] });
      setShowCreateForm(false);
      setNewGroup({ name: '', description: '', group_type: 'general', is_public: true });
      toast.success('Grupo creado exitosamente');
    },
  });

  const joinGroupMutation = useMutation({
    mutationFn: (groupId) => base44.entities.GroupMembership.create({
      group_id: groupId,
      user_id: user.id,
      role: 'member'
    }),
    onSuccess: (data, groupId) => {
      queryClient.invalidateQueries({ queryKey: ['group-memberships'] });
      base44.entities.Group.update(groupId, { 
        member_count: (groups.find(g => g.id === groupId)?.member_count || 0) + 1 
      });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Te uniste al grupo');
    },
  });

  const handleCreateGroup = () => {
    if (!newGroup.name.trim()) {
      toast.error('El nombre del grupo es requerido');
      return;
    }
    createGroupMutation.mutate(newGroup);
  };

  const isUserInGroup = (groupId) => {
    return memberships.some(m => m.group_id === groupId);
  };

  const getGroupIcon = (type) => {
    switch(type) {
      case 'health_condition': return '🏥';
      case 'dietary_preference': return '🥗';
      default: return '💬';
    }
  };

  const suggestedGroups = [
    { name: 'Diabéticos Unidos', type: 'health_condition', condition: 'diabetes', description: 'Comparte tips y recetas para el control de la diabetes' },
    { name: 'Hipertensos Saludables', type: 'health_condition', condition: 'hipertension', description: 'Consejos para reducir el sodio y mantener la presión bajo control' },
    { name: 'Vida Sin TACC', type: 'dietary_preference', condition: 'celiaco', description: 'Productos sin gluten y recetas para celíacos' },
    { name: 'Alimentación Consciente', type: 'general', description: 'Para todos los que buscan comer más saludable' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-gray-50 pb-20">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-lg">Grupos</h1>
                <p className="text-xs text-gray-400">Conéctate con otros usuarios</p>
              </div>
            </div>
            <InviteButton variant="compact" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Crear nuevo grupo
          </Button>
        </motion.div>

        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Crear Grupo</h3>
              <div className="space-y-4">
                <Input
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="Nombre del grupo"
                />
                <Textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  placeholder="Descripción del grupo"
                  rows={3}
                />
                <div className="flex gap-3">
                  <Button onClick={handleCreateGroup} className="flex-1">
                    Crear Grupo
                  </Button>
                  <Button onClick={() => setShowCreateForm(false)} variant="outline">
                    Cancelar
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <div className="mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Grupos Sugeridos</h2>
          <div className="grid gap-4">
            {suggestedGroups.map((group, index) => {
              const existingGroup = groups.find(g => g.name === group.name);
              const groupId = existingGroup?.id;
              const isMember = groupId && isUserInGroup(groupId);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="text-4xl">{getGroupIcon(group.type)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{group.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {existingGroup?.member_count || 0} miembros
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            {existingGroup?.is_public !== false ? (
                              <><Globe className="w-3 h-3" /> Público</>
                            ) : (
                              <><Lock className="w-3 h-3" /> Privado</>
                            )}
                          </span>
                        </div>
                      </div>
                      {isMember ? (
                        <Button size="sm" variant="outline" disabled>
                          Miembro
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => {
                            if (groupId) {
                              joinGroupMutation.mutate(groupId);
                            } else {
                              createGroupMutation.mutate({
                                name: group.name,
                                description: group.description,
                                group_type: group.type,
                                related_condition: group.condition,
                                is_public: true,
                                member_count: 1
                              });
                            }
                          }}
                        >
                          Unirse
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {groups.filter(g => !suggestedGroups.some(sg => sg.name === g.name)).length > 0 && (
          <div>
            <h2 className="font-semibold text-gray-900 mb-4">Todos los Grupos</h2>
            <div className="grid gap-4">
              {groups
                .filter(g => !suggestedGroups.some(sg => sg.name === g.name))
                .map((group) => {
                  const isMember = isUserInGroup(group.id);
                  return (
                    <Card key={group.id} className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="text-4xl">{getGroupIcon(group.group_type)}</div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900">{group.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                          <div className="flex items-center gap-3 mt-3">
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {group.member_count || 0} miembros
                            </span>
                          </div>
                        </div>
                        {isMember ? (
                          <Button size="sm" variant="outline" disabled>
                            Miembro
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => joinGroupMutation.mutate(group.id)}>
                            Unirse
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}